import { TEAM_AMBIENT_LANDMARK_IDS, TEAM_THEME_FLAVORS } from './team-ambient-landmarks';

/*
 * Per-team theme registry — 48 World Cup 2026 nations.
 *
 * Pure data, safe to import from server, web (Vite), and native (Metro).
 * Keyed by `fifaCode` (matches `TeamPublic.fifaCode` and the seeded DB).
 *
 * When the user picks a favorite team, the client renders a "team takeover"
 * on top of the neutral Continental '26 surfaces — a layered ambient backdrop,
 * a sidebar color wash, a greeting pill, and a per-team display font on the
 * Home / Profile / Match Detail hero. The rest of the app stays neutral so
 * text legibility never depends on any single palette.
 *
 * Match-detail *landmark halos* (see team-landmarks.ts) render for ALL
 * matches independently of the user's favorite team — they're a head-to-head
 * decoration keyed off the two matching teams.
 *
 * Handoff design fidelity: this file mirrors `themes.js` from the Claude
 * Design bundle (`Mondy 48 Team Skins.html`). Colors pulled from known flag
 * tokens; display fonts are Google Fonts with a Bebas Neue / Inter fallback.
 */

export type TeamPaletteMode = 'light' | 'dark';

export interface TeamPalette {
  /**
   * Whether the flag is predominantly light (white/cream base) or dark.
   * Light-mode teams use `ink` as the backdrop base instead of `deep` —
   * pale `deep` colors blow out the whole pane otherwise.
   */
  mode: TeamPaletteMode;
  /** Main team color — accent fills, H1 emphasis, active tab. Hex. */
  primary: string;
  /** Secondary team color — gradient ends, secondary chrome. Hex. */
  accent: string;
  /** Deep base — hero card background for dark-mode teams. Hex. */
  deep: string;
  /** Alias, usually = primary. Used for tint washes. Hex. */
  tint: string;
  /** Darkest variant — bg base for light-mode teams. Hex. Optional. */
  ink?: string;
}

export type FlagPatternSpec =
  | { kind: 'hStripes'; colors: string[] }
  | { kind: 'vStripes'; colors: string[] }
  | { kind: 'cross'; bg: string; stroke: string; offsetL: number; offsetT: number }
  | { kind: 'saltire'; bg: string; stroke: string }
  | { kind: 'sun'; bg: string; disc: string; ringCount: number }
  | { kind: 'taegeuk'; bg: string; red: string; blue: string; trigram: string }
  | { kind: 'crescent'; bg: string; stroke: string }
  | { kind: 'blocks'; tl: string; tr: string; bl: string; br: string }
  | { kind: 'unionJack'; bg: string; cross1: string; cross2: string }
  | { kind: 'centerEmblem'; left: string; center: string; right: string; emblem: string }
  | { kind: 'diagonal'; a: string; b: string }
  | { kind: 'checker'; a: string; b: string }
  | { kind: 'starCenter'; bg: string; starColor: string };

const FP = {
  hStripes: (...colors: string[]): FlagPatternSpec => ({ kind: 'hStripes', colors }),
  vStripes: (...colors: string[]): FlagPatternSpec => ({ kind: 'vStripes', colors }),
  cross: (bg: string, stroke: string, offsetL = 0.33, offsetT = 0.45): FlagPatternSpec => ({
    kind: 'cross',
    bg,
    stroke,
    offsetL,
    offsetT,
  }),
  saltire: (bg: string, stroke: string): FlagPatternSpec => ({
    kind: 'saltire',
    bg,
    stroke,
  }),
  sun: (bg: string, disc: string, ringCount = 0): FlagPatternSpec => ({
    kind: 'sun',
    bg,
    disc,
    ringCount,
  }),
  taegeuk: (bg: string, red: string, blue: string, trigram: string): FlagPatternSpec => ({
    kind: 'taegeuk',
    bg,
    red,
    blue,
    trigram,
  }),
  crescent: (bg: string, stroke: string): FlagPatternSpec => ({
    kind: 'crescent',
    bg,
    stroke,
  }),
  blocks: (tl: string, tr: string, bl: string, br: string): FlagPatternSpec => ({
    kind: 'blocks',
    tl,
    tr,
    bl,
    br,
  }),
  unionJack: (bg: string, cross1: string, cross2: string): FlagPatternSpec => ({
    kind: 'unionJack',
    bg,
    cross1,
    cross2,
  }),
  centerEmblem: (
    left: string,
    center: string,
    right: string,
    emblem: string,
  ): FlagPatternSpec => ({ kind: 'centerEmblem', left, center, right, emblem }),
  diagonal: (a: string, b: string): FlagPatternSpec => ({ kind: 'diagonal', a, b }),
  checker: (a: string, b: string): FlagPatternSpec => ({ kind: 'checker', a, b }),
  starCenter: (bg: string, starColor: string): FlagPatternSpec => ({
    kind: 'starCenter',
    bg,
    starColor,
  }),
};

export interface TeamTheme {
  /** 3-letter FIFA code — the stable key that matches `TeamPublic.fifaCode`. */
  code: string;
  /** Display country name as it appears in the handoff (not necessarily the seed's name). */
  country: string;
  /** Regional-indicator flag emoji — rendered large on match-detail hero. */
  flag: string;
  /** Group letter A–L (48-team WC26 format, 12 groups of 4). */
  group: string;
  palette: TeamPalette;
  pattern: FlagPatternSpec;
  /** Ambient backdrop landmark key from `team-ambient-landmarks`. */
  landmark?: string;
  /** Optional handoff visual flavor tag for future flourish tuning. */
  flavor?: string;
  /** Short native-language greeting — rendered in a pill above the hero. */
  greeting: string;
  /** Display font family (Google Fonts name). Falls back to Bebas Neue / Inter. */
  font: string;
}

export const TEAM_THEMES: Record<string, TeamTheme> = {
  // ---------- Group A ----------
  CZE: {
    code: 'CZE',
    country: 'Czechia',
    flag: '🇨🇿',
    group: 'A',
    palette: { mode: 'dark', primary: '#D7141A', accent: '#11457E', deep: '#1a1128', tint: '#11457E' },
    pattern: FP.hStripes('#FFFFFF', '#D7141A', '#11457E'),
    greeting: 'Do toho!',
    font: 'Oswald',
  },
  KOR: {
    code: 'KOR',
    country: 'South Korea',
    flag: '🇰🇷',
    group: 'A',
    palette: { mode: 'dark', primary: '#CD2E3A', accent: '#0047A0', deep: '#14131f', tint: '#CD2E3A' },
    pattern: FP.taegeuk('#FFFFFF', '#CD2E3A', '#0047A0', '#1A1A1A'),
    greeting: '화이팅!',
    font: 'Noto Serif KR',
  },
  MEX: {
    code: 'MEX',
    country: 'Mexico',
    flag: '🇲🇽',
    group: 'A',
    palette: { mode: 'dark', primary: '#006847', accent: '#CE1126', deep: '#0d1d14', tint: '#CE1126' },
    pattern: FP.vStripes('#006847', '#FFFFFF', '#CE1126'),
    greeting: '¡Vamos!',
    font: 'Cormorant Garamond',
  },
  RSA: {
    code: 'RSA',
    country: 'South Africa',
    flag: '🇿🇦',
    group: 'A',
    palette: { mode: 'dark', primary: '#FFB612', accent: '#007749', deep: '#132318', tint: '#FFB612' },
    pattern: FP.hStripes('#007749', '#FFFFFF', '#007749'),
    greeting: 'Bafana Bafana!',
    font: 'Oswald',
  },

  // ---------- Group B ----------
  BIH: {
    code: 'BIH',
    country: 'Bosnia & Herzegovina',
    flag: '🇧🇦',
    group: 'B',
    palette: { mode: 'dark', primary: '#FECB00', accent: '#002395', deep: '#0f142a', tint: '#FECB00' },
    pattern: FP.diagonal('#002395', '#FECB00'),
    greeting: 'Zmajevi!',
    font: 'Oswald',
  },
  CAN: {
    code: 'CAN',
    country: 'Canada',
    flag: '🇨🇦',
    group: 'B',
    palette: {
      mode: 'light',
      primary: '#D52B1E',
      accent: '#1A1A1A',
      deep: '#f7e8e6',
      tint: '#D52B1E',
      ink: '#1A1A1A',
    },
    pattern: FP.centerEmblem('#D52B1E', '#FFFFFF', '#D52B1E', 'maple'),
    greeting: 'Allez les Rouges!',
    font: 'Playfair Display',
  },
  QAT: {
    code: 'QAT',
    country: 'Qatar',
    flag: '🇶🇦',
    group: 'B',
    palette: { mode: 'dark', primary: '#8A1538', accent: '#E0C68A', deep: '#1e0f18', tint: '#8A1538' },
    pattern: FP.vStripes('#F5F1E6', '#8A1538', '#8A1538'),
    greeting: 'هيا قطر!',
    font: 'Amiri',
  },
  SUI: {
    code: 'SUI',
    country: 'Switzerland',
    flag: '🇨🇭',
    group: 'B',
    // Inverted per the design chat (primary cream → accent red swap).
    palette: { mode: 'dark', primary: '#F5F1E6', accent: '#DA291C', deep: '#200f0f', tint: '#F5F1E6' },
    pattern: FP.cross('#DA291C', '#FFFFFF', 0.5, 0.5),
    greeting: 'Hopp Schwiiz!',
    font: 'Inter',
  },

  // ---------- Group C ----------
  BRA: {
    code: 'BRA',
    country: 'Brazil',
    flag: '🇧🇷',
    group: 'C',
    palette: { mode: 'dark', primary: '#FEDF00', accent: '#009C3B', deep: '#0b1d12', tint: '#009C3B' },
    pattern: FP.blocks('#009C3B', '#FEDF00', '#FEDF00', '#009C3B'),
    greeting: 'Vai Brasil!',
    font: 'Bebas Neue',
  },
  HAI: {
    code: 'HAI',
    country: 'Haiti',
    flag: '🇭🇹',
    group: 'C',
    palette: { mode: 'dark', primary: '#00209F', accent: '#D21034', deep: '#0a0f26', tint: '#D21034' },
    pattern: FP.hStripes('#00209F', '#D21034'),
    greeting: 'Ann ale!',
    font: 'Playfair Display',
  },
  MAR: {
    code: 'MAR',
    country: 'Morocco',
    flag: '🇲🇦',
    group: 'C',
    palette: { mode: 'dark', primary: '#C1272D', accent: '#006233', deep: '#1a0b0d', tint: '#C1272D' },
    pattern: FP.starCenter('#C1272D', '#006233'),
    greeting: 'يالله المغرب!',
    font: 'Amiri',
  },
  SCO: {
    code: 'SCO',
    country: 'Scotland',
    flag: '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
    group: 'C',
    // Inverted per the design chat.
    palette: { mode: 'dark', primary: '#FFFFFF', accent: '#0065BD', deep: '#0b1326', tint: '#FFFFFF' },
    pattern: FP.saltire('#0065BD', '#FFFFFF'),
    greeting: 'Come on Scotland!',
    font: 'Playfair Display',
  },

  // ---------- Group D ----------
  AUS: {
    code: 'AUS',
    country: 'Australia',
    flag: '🇦🇺',
    group: 'D',
    palette: { mode: 'dark', primary: '#FFCD00', accent: '#00843D', deep: '#0f1d13', tint: '#FFCD00' },
    pattern: FP.unionJack('#012169', '#FFFFFF', '#E4002B'),
    greeting: 'Come on Socceroos!',
    font: 'Oswald',
  },
  PAR: {
    code: 'PAR',
    country: 'Paraguay',
    flag: '🇵🇾',
    group: 'D',
    palette: { mode: 'dark', primary: '#D52B1E', accent: '#0038A8', deep: '#200f0f', tint: '#D52B1E' },
    pattern: FP.hStripes('#D52B1E', '#FFFFFF', '#0038A8'),
    greeting: '¡Vamos Albirroja!',
    font: 'Cormorant Garamond',
  },
  TUR: {
    code: 'TUR',
    country: 'Türkiye',
    flag: '🇹🇷',
    group: 'D',
    // Inverted per the design chat.
    palette: { mode: 'dark', primary: '#F5F1E6', accent: '#E30A17', deep: '#1e0a0d', tint: '#F5F1E6' },
    pattern: FP.crescent('#E30A17', '#FFFFFF'),
    greeting: 'Bir milletin tutkusu!',
    font: 'Amiri',
  },
  USA: {
    code: 'USA',
    country: 'United States',
    flag: '🇺🇸',
    group: 'D',
    palette: { mode: 'dark', primary: '#BF0A30', accent: '#002868', deep: '#0b1128', tint: '#BF0A30' },
    pattern: FP.hStripes('#BF0A30', '#FFFFFF', '#BF0A30', '#FFFFFF', '#BF0A30'),
    greeting: "Let's go USA!",
    font: 'Oswald',
  },

  // ---------- Group E ----------
  CIV: {
    code: 'CIV',
    country: "Côte d'Ivoire",
    flag: '🇨🇮',
    group: 'E',
    palette: { mode: 'dark', primary: '#F77F00', accent: '#009E60', deep: '#1e130a', tint: '#F77F00' },
    pattern: FP.vStripes('#F77F00', '#FFFFFF', '#009E60'),
    greeting: 'Allez les Éléphants!',
    font: 'Oswald',
  },
  CUW: {
    code: 'CUW',
    country: 'Curaçao',
    flag: '🇨🇼',
    group: 'E',
    // Inverted per the design chat.
    palette: { mode: 'dark', primary: '#F9E814', accent: '#002B7F', deep: '#0a1028', tint: '#002B7F' },
    pattern: FP.hStripes('#002B7F', '#F9E814', '#002B7F'),
    greeting: 'Dushi Curaçao!',
    font: 'Playfair Display',
  },
  ECU: {
    code: 'ECU',
    country: 'Ecuador',
    flag: '🇪🇨',
    group: 'E',
    palette: { mode: 'dark', primary: '#FFDD00', accent: '#0072CE', deep: '#1e1a07', tint: '#FFDD00' },
    pattern: FP.hStripes('#FFDD00', '#FFDD00', '#0072CE', '#ED1C24'),
    greeting: '¡Vamos La Tri!',
    font: 'Cormorant Garamond',
  },
  GER: {
    code: 'GER',
    country: 'Germany',
    flag: '🇩🇪',
    group: 'E',
    // Inverted per the design chat.
    palette: { mode: 'dark', primary: '#FFCE00', accent: '#DD0000', deep: '#1a0f0f', tint: '#FFCE00' },
    pattern: FP.hStripes('#000000', '#DD0000', '#FFCE00'),
    greeting: 'Die Mannschaft!',
    font: 'Oswald',
  },

  // ---------- Group F ----------
  JPN: {
    code: 'JPN',
    country: 'Japan',
    flag: '🇯🇵',
    group: 'F',
    palette: {
      mode: 'light',
      primary: '#BC002D',
      accent: '#1A1A1A',
      deep: '#f7eaec',
      tint: '#BC002D',
      ink: '#1A1A1A',
    },
    pattern: FP.sun('#FFFFFF', '#BC002D', 0),
    greeting: '頑張れ日本！',
    font: 'Noto Serif JP',
  },
  NED: {
    code: 'NED',
    country: 'Netherlands',
    flag: '🇳🇱',
    group: 'F',
    palette: { mode: 'dark', primary: '#FF6B1A', accent: '#21468B', deep: '#1e1308', tint: '#FF6B1A' },
    pattern: FP.hStripes('#AE1C28', '#FFFFFF', '#21468B'),
    greeting: 'Hup Holland!',
    font: 'Oswald',
  },
  SWE: {
    code: 'SWE',
    country: 'Sweden',
    flag: '🇸🇪',
    group: 'F',
    palette: { mode: 'dark', primary: '#FECC00', accent: '#006AA7', deep: '#0a1028', tint: '#FECC00' },
    pattern: FP.cross('#006AA7', '#FECC00', 0.3, 0.5),
    greeting: 'Heja Sverige!',
    font: 'Oswald',
  },
  TUN: {
    code: 'TUN',
    country: 'Tunisia',
    flag: '🇹🇳',
    group: 'F',
    // Inverted per the design chat.
    palette: { mode: 'dark', primary: '#F5F1E6', accent: '#E70013', deep: '#1e0a0a', tint: '#F5F1E6' },
    pattern: FP.crescent('#E70013', '#FFFFFF'),
    greeting: 'يا نسور قرطاج!',
    font: 'Amiri',
  },

  // ---------- Group G ----------
  BEL: {
    code: 'BEL',
    country: 'Belgium',
    flag: '🇧🇪',
    group: 'G',
    palette: { mode: 'dark', primary: '#ED2939', accent: '#FFCD00', deep: '#1e0c0f', tint: '#FFCD00' },
    pattern: FP.vStripes('#1A1A1A', '#FFCD00', '#ED2939'),
    greeting: 'Rode Duivels!',
    font: 'Oswald',
  },
  EGY: {
    code: 'EGY',
    country: 'Egypt',
    flag: '🇪🇬',
    group: 'G',
    palette: { mode: 'dark', primary: '#CE1126', accent: '#D4AF37', deep: '#1a0a0d', tint: '#D4AF37' },
    pattern: FP.hStripes('#CE1126', '#FFFFFF', '#1A1A1A'),
    greeting: 'يلا يا مصر!',
    font: 'Amiri',
  },
  IRN: {
    code: 'IRN',
    country: 'Iran',
    flag: '🇮🇷',
    group: 'G',
    palette: { mode: 'dark', primary: '#239F40', accent: '#DA0000', deep: '#0b1d12', tint: '#239F40' },
    pattern: FP.hStripes('#239F40', '#FFFFFF', '#DA0000'),
    greeting: 'ایران!',
    font: 'Amiri',
  },
  NZL: {
    code: 'NZL',
    country: 'New Zealand',
    flag: '🇳🇿',
    group: 'G',
    palette: { mode: 'dark', primary: '#00247D', accent: '#CC142B', deep: '#07102a', tint: '#00247D' },
    pattern: FP.unionJack('#00247D', '#FFFFFF', '#CC142B'),
    greeting: 'Kia kaha!',
    font: 'Oswald',
  },

  // ---------- Group H ----------
  CPV: {
    code: 'CPV',
    country: 'Cape Verde',
    flag: '🇨🇻',
    group: 'H',
    palette: { mode: 'dark', primary: '#003893', accent: '#CF2027', deep: '#060e2a', tint: '#003893' },
    pattern: FP.hStripes('#003893', '#FFFFFF', '#CF2027', '#FFFFFF', '#003893'),
    greeting: 'Força Tubarões Azuis!',
    font: 'Playfair Display',
  },
  ESP: {
    code: 'ESP',
    country: 'Spain',
    flag: '🇪🇸',
    group: 'H',
    // Inverted per the design chat.
    palette: { mode: 'dark', primary: '#F1BF00', accent: '#AA151B', deep: '#1e0a0c', tint: '#F1BF00' },
    pattern: FP.hStripes('#AA151B', '#F1BF00', '#F1BF00', '#AA151B'),
    greeting: '¡Vamos La Roja!',
    font: 'Playfair Display',
  },
  KSA: {
    code: 'KSA',
    country: 'Saudi Arabia',
    flag: '🇸🇦',
    group: 'H',
    // Inverted per the design chat.
    palette: { mode: 'dark', primary: '#F5F1E6', accent: '#006C35', deep: '#07190e', tint: '#F5F1E6' },
    pattern: FP.starCenter('#006C35', '#FFFFFF'),
    greeting: 'الأخضر!',
    font: 'Amiri',
  },
  URU: {
    code: 'URU',
    country: 'Uruguay',
    flag: '🇺🇾',
    group: 'H',
    palette: { mode: 'dark', primary: '#7FB3D5', accent: '#FCD116', deep: '#0e1a26', tint: '#7FB3D5' },
    pattern: FP.hStripes(
      '#FFFFFF',
      '#7FB3D5',
      '#FFFFFF',
      '#7FB3D5',
      '#FFFFFF',
      '#7FB3D5',
      '#FFFFFF',
      '#7FB3D5',
      '#FFFFFF',
    ),
    greeting: '¡Vamos Celeste!',
    font: 'Cormorant Garamond',
  },

  // ---------- Group I ----------
  FRA: {
    code: 'FRA',
    country: 'France',
    flag: '🇫🇷',
    group: 'I',
    palette: { mode: 'dark', primary: '#0055A4', accent: '#EF4135', deep: '#060f26', tint: '#0055A4' },
    pattern: FP.vStripes('#0055A4', '#FFFFFF', '#EF4135'),
    greeting: 'Allez les Bleus!',
    font: 'Playfair Display',
  },
  IRQ: {
    code: 'IRQ',
    country: 'Iraq',
    flag: '🇮🇶',
    group: 'I',
    palette: { mode: 'dark', primary: '#CE1126', accent: '#007A3D', deep: '#1a0a0d', tint: '#CE1126' },
    pattern: FP.hStripes('#CE1126', '#FFFFFF', '#1A1A1A'),
    greeting: 'أسود الرافدين!',
    font: 'Amiri',
  },
  NOR: {
    code: 'NOR',
    country: 'Norway',
    flag: '🇳🇴',
    group: 'I',
    palette: { mode: 'dark', primary: '#BA0C2F', accent: '#00205B', deep: '#1a0a0f', tint: '#BA0C2F' },
    pattern: FP.cross('#BA0C2F', '#00205B', 0.3, 0.5),
    greeting: 'Heia Norge!',
    font: 'Oswald',
  },
  SEN: {
    code: 'SEN',
    country: 'Senegal',
    flag: '🇸🇳',
    group: 'I',
    // Inverted per the design chat.
    palette: { mode: 'dark', primary: '#FDEF42', accent: '#00853F', deep: '#07190e', tint: '#00853F' },
    pattern: FP.vStripes('#00853F', '#FDEF42', '#E31B23'),
    greeting: 'Allez les Lions!',
    font: 'Oswald',
  },

  // ---------- Group J ----------
  ALG: {
    code: 'ALG',
    country: 'Algeria',
    flag: '🇩🇿',
    group: 'J',
    palette: { mode: 'dark', primary: '#006233', accent: '#D21034', deep: '#07190e', tint: '#006233' },
    pattern: FP.vStripes('#006233', '#FFFFFF'),
    greeting: 'تحيا الخضر!',
    font: 'Amiri',
  },
  ARG: {
    code: 'ARG',
    country: 'Argentina',
    flag: '🇦🇷',
    group: 'J',
    palette: {
      mode: 'light',
      primary: '#6CACE4',
      accent: '#F6B40E',
      deep: '#e8f2fb',
      tint: '#6CACE4',
      ink: '#0b1a2e',
    },
    pattern: FP.sun('#FFFFFF', '#F6B40E', 16),
    greeting: '¡Vamos Argentina!',
    font: 'Playfair Display',
  },
  AUT: {
    code: 'AUT',
    country: 'Austria',
    flag: '🇦🇹',
    group: 'J',
    // Inverted per the design chat.
    palette: { mode: 'dark', primary: '#F5F1E6', accent: '#ED2939', deep: '#1e0a0c', tint: '#F5F1E6' },
    pattern: FP.hStripes('#ED2939', '#FFFFFF', '#ED2939'),
    greeting: "Auf geht's Österreich!",
    font: 'Playfair Display',
  },
  JOR: {
    code: 'JOR',
    country: 'Jordan',
    flag: '🇯🇴',
    group: 'J',
    palette: { mode: 'dark', primary: '#007A3D', accent: '#CE1126', deep: '#07190e', tint: '#CE1126' },
    pattern: FP.hStripes('#1A1A1A', '#FFFFFF', '#007A3D'),
    greeting: 'النشامى!',
    font: 'Amiri',
  },

  // ---------- Group K ----------
  COD: {
    code: 'COD',
    country: 'DR Congo',
    flag: '🇨🇩',
    group: 'K',
    // Inverted per the design chat.
    palette: { mode: 'dark', primary: '#F7D618', accent: '#007FFF', deep: '#051a33', tint: '#007FFF' },
    pattern: FP.diagonal('#007FFF', '#F7D618'),
    greeting: 'Léopards!',
    font: 'Oswald',
  },
  COL: {
    code: 'COL',
    country: 'Colombia',
    flag: '🇨🇴',
    group: 'K',
    palette: { mode: 'dark', primary: '#FCD116', accent: '#003893', deep: '#1e1a07', tint: '#FCD116' },
    pattern: FP.hStripes('#FCD116', '#FCD116', '#003893', '#CE1126'),
    greeting: '¡Vamos Colombia!',
    font: 'Cormorant Garamond',
  },
  POR: {
    code: 'POR',
    country: 'Portugal',
    flag: '🇵🇹',
    group: 'K',
    palette: { mode: 'dark', primary: '#046A38', accent: '#DA291C', deep: '#07190e', tint: '#DA291C' },
    pattern: FP.vStripes('#046A38', '#DA291C', '#DA291C', '#DA291C'),
    greeting: 'Força Portugal!',
    font: 'Playfair Display',
  },
  UZB: {
    code: 'UZB',
    country: 'Uzbekistan',
    flag: '🇺🇿',
    group: 'K',
    palette: { mode: 'dark', primary: '#0099B5', accent: '#1EB53A', deep: '#05212e', tint: '#0099B5' },
    pattern: FP.hStripes('#0099B5', '#FFFFFF', '#1EB53A'),
    greeting: "Oq yo'l!",
    font: 'Amiri',
  },

  // ---------- Group L ----------
  CRO: {
    code: 'CRO',
    country: 'Croatia',
    flag: '🇭🇷',
    group: 'L',
    // Inverted per the design chat.
    palette: { mode: 'dark', primary: '#F5F1E6', accent: '#FF0000', deep: '#1e0a0a', tint: '#F5F1E6' },
    pattern: FP.checker('#FF0000', '#FFFFFF'),
    greeting: 'Vatreni!',
    font: 'Oswald',
  },
  ENG: {
    code: 'ENG',
    country: 'England',
    flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
    group: 'L',
    // Inverted per the design chat.
    palette: { mode: 'dark', primary: '#F5F1E6', accent: '#CE1124', deep: '#1e0a0d', tint: '#F5F1E6' },
    pattern: FP.cross('#FFFFFF', '#CE1124', 0.5, 0.5),
    greeting: "It's coming home.",
    font: 'Playfair Display',
  },
  GHA: {
    code: 'GHA',
    country: 'Ghana',
    flag: '🇬🇭',
    group: 'L',
    // Inverted per the design chat.
    palette: { mode: 'dark', primary: '#FCD116', accent: '#CE1126', deep: '#1a0a0d', tint: '#FCD116' },
    pattern: FP.hStripes('#CE1126', '#FCD116', '#006B3F'),
    greeting: 'Black Stars!',
    font: 'Oswald',
  },
  PAN: {
    code: 'PAN',
    country: 'Panama',
    flag: '🇵🇦',
    group: 'L',
    palette: { mode: 'dark', primary: '#D21034', accent: '#005AA7', deep: '#200f12', tint: '#D21034' },
    pattern: FP.blocks('#FFFFFF', '#D21034', '#005AA7', '#FFFFFF'),
    greeting: '¡Vamos La Roja!',
    font: 'Oswald',
  },
};

/** Neutral fallback used when a team has no theme registered (shouldn't happen for WC26). */
export const NEUTRAL_THEME: TeamTheme = {
  code: 'NEUTRAL',
  country: 'Mondy',
  flag: '🏁',
  group: '',
  palette: {
    mode: 'dark',
    primary: '#FF6F4A',
    accent: '#78C5A0',
    deep: '#1a0e09',
    tint: '#FF6F4A',
  },
  pattern: FP.hStripes('#1a1a1a', '#2a2a2a'),
  greeting: 'Let the games begin.',
  font: 'Bebas Neue',
};

for (const theme of Object.values(TEAM_THEMES)) {
  theme.landmark = TEAM_AMBIENT_LANDMARK_IDS[theme.code];
  theme.flavor = TEAM_THEME_FLAVORS[theme.code];
}

/**
 * Resolve a fifaCode to its theme. Returns null when the code isn't mapped
 * — callers should fall through to the neutral Continental '26 surfaces
 * rather than substituting a stand-in (the advisor called this out).
 */
export function getTeamTheme(fifaCode: string | null | undefined): TeamTheme | null {
  if (!fifaCode) return null;
  return TEAM_THEMES[fifaCode.toUpperCase()] ?? null;
}
